import { Injectable, inject, signal } from '@angular/core';
import { PlatformDetectorService } from '../services/util/platform-detector.service';

/**
 * Shared API configuration service that manages the base path for all API versions.
 * All versioned API clients use this service to maintain consistent configuration.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiConfigurationService {
  private readonly apiBasePathKey = 'apiBasePath';
  private readonly _basePath = signal<string>('');

  private readonly platformDetectorService = inject(PlatformDetectorService);

  constructor() {
    // Upgrade check: Remove deprecated base paths
    const storedPath = localStorage.getItem(this.apiBasePathKey);
    if (
      storedPath &&
      (storedPath.includes('heizung.hilaren.de') ||
        storedPath.includes('boger.templado.app'))
    ) {
      console.log(
        'Detected deprecated API base path. Resetting to default base path.'
      );
      this.resetBasePath();
    }

    const storedBasePath = localStorage.getItem(this.apiBasePathKey) || '';
    if (!storedBasePath) {
      console.log(
        'No API base path found in local storage. Using default base path.'
      );
      const defaultPath = this.getDefaultBasePath();
      this._basePath.set(defaultPath);
      localStorage.setItem(this.apiBasePathKey, defaultPath);
    } else {
      this._basePath.set(storedBasePath);
    }
  }

  /**
   * Get the current API base path.
   */
  public getBasePath(): string {
    return this._basePath();
  }

  /**
   * Get the base path as a readonly signal.
   */
  public readonly basePath = this._basePath.asReadonly();

  /**
   * Set a new API base path. This affects all versioned API clients.
   */
  public setBasePath(basePath: string): void {
    this._basePath.set(basePath);
    localStorage.setItem(this.apiBasePathKey, basePath);
  }

  /**
   * Reset the API base path to the default value.
   */
  public resetBasePath(): void {
    localStorage.removeItem(this.apiBasePathKey);
    const defaultPath = this.getDefaultBasePath();
    this._basePath.set(defaultPath);
    localStorage.setItem(this.apiBasePathKey, defaultPath);
  }

  /**
   * Get the default API base path based on the platform.
   */
  public getDefaultBasePath(): string {
    if (this.platformDetectorService.isRunningAsApp) {
      return 'https://web.caldevo.app';
    } else {
      // PWA: Get base path from current location
      return (
        globalThis.location.protocol +
        '//' +
        globalThis.location.hostname +
        (globalThis.location.port ? ':' + globalThis.location.port : '')
      );
    }
  }
}
